import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { Ban, PencilLine, Undo2 } from "lucide-react";
import { RefundTracker } from "@/components/RefundTracker";

const inp = "w-full rounded-xl border border-navy/15 bg-white px-3 py-2 text-sm outline-none focus:border-orange";

export function ReviseEstimate({ id, onDone }) {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const send = async () => {
        setBusy(true);
        try { await api.post(`/bookings/${id}/estimate-revision`, { note }); toast.success("Sent to your professional for correction"); setOpen(false); await onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    if (!open) return <button data-testid="revise-estimate-btn" onClick={() => setOpen(true)} className="flex w-full items-center justify-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 py-2.5 text-sm font-bold text-amber-800"><PencilLine size={15} /> Charges look wrong — request revision</button>;
    return (
        <div className="space-y-2 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="revise-estimate-form">
            <textarea data-testid="revise-estimate-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What looks wrong? e.g. labour seems high for one switch" className={inp} />
            <div className="flex gap-2">
                <button data-testid="revise-estimate-submit" disabled={busy || note.trim().length < 5} onClick={send} className="flex-1 rounded-full bg-amber-500 py-2 text-sm font-bold text-white disabled:opacity-50">Send for revision</button>
                <button onClick={() => setOpen(false)} className="rounded-full border border-navy/15 px-4 text-sm font-bold text-navy/60">Close</button>
            </div>
        </div>
    );
}

// Customer cancel with clear refund rules + refund status list.
export function CustomerCancel({ b, onDone }) {
    const [pol, setPol] = useState(null);
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get(`/bookings/${b.id}/cancel-policy`).then((r) => setPol(r.data)).catch(() => {}); }, [b.id, b.status]);
    const cancel = async () => {
        setBusy(true);
        try { const { data } = await api.post(`/bookings/${b.id}/cancel`, { reason: reason || "Customer cancelled" }); toast.success(data.refund ? `Cancelled — refund of ${rupee(data.refund.amount)} requested` : "Booking cancelled"); setOpen(false); await onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <>
            {b.refunds?.length > 0 && (
                <div data-testid="customer-refunds" className="space-y-2">
                    <p className="flex items-center gap-1.5 px-1 font-heading text-sm font-bold text-navy"><Undo2 size={15} /> Refund progress</p>
                    {b.refunds.map((r) => <RefundTracker key={r.id} r={r} />)}
                </div>
            )}
            {pol?.can_cancel && (
                <div data-testid="customer-cancel-box" className="text-center">
                    {!open ? (
                        <button data-testid="customer-cancel-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500"><Ban size={15} /> Cancel booking</button>
                    ) : (
                        <div className="space-y-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-left">
                            <p data-testid="customer-cancel-policy" className={`text-xs font-semibold ${pol.refundable ? "text-emerald-700" : "text-red-700"}`}>{pol.message}</p>
                            <input data-testid="customer-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className={inp} />
                            <div className="flex gap-2">
                                <button data-testid="customer-cancel-confirm" disabled={busy} onClick={cancel} className="flex-1 rounded-full bg-red-500 py-2.5 text-sm font-bold text-white disabled:opacity-60">{pol.refundable ? "Cancel & request refund" : "Cancel (not refundable)"}</button>
                                <button onClick={() => setOpen(false)} className="rounded-full border border-navy/15 px-4 text-sm font-bold text-navy/60">Keep</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
